import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { api } from "./api";
import { Field, Status, Pager, date, today, useData } from "./common";
const key = () => window.crypto.randomUUID();
export default function Inventory({ canOperate }) {
  const [until, setUntil] = useState(today()),
    [revision, setRevision] = useState(0);
  const [form, setForm] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [message, setMessage] = useState(""),
    [page, setPage] = useState(1);
  const saving = useRef(false);
  const balances = useData("inventario", { hasta: until }, revision);
  const history = useData(
    "inventario/movimientos",
    { hasta: until, page },
    revision
  );
  function open(row, type) {
    setError("");
    setMessage("");
    setForm({
      code: row.code,
      name: row.name,
      unit: row.unit,
      type,
      quantity: "",
      operationDate: today(),
      reason: "",
      requestKey: key(),
    });
  }
  function change(field, value) {
    setForm((previous) => ({ ...previous, [field]: value, requestKey: key() }));
  }
  async function save(event) {
    event.preventDefault();
    if (saving.current) return;
    if (form.quantity === "" || !form.reason.trim()) {
      setError("Indique la cantidad y el motivo.");
      return;
    }
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      const { name, ...body } = form;
      await api("inventario/movimientos", "POST", {
        ...body,
        quantity: Number(body.quantity),
      });
      setUntil(form.operationDate > until ? form.operationDate : until);
      setMessage(
        `${
          form.type === "CONTEO_FISICO" ? "Conteo físico" : "Entrada"
        } registrado para ${name}.`
      );
      setForm(null);
      setRevision((n) => n + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="sd-inventory">
      <h2>Inventario de bodega</h2>
      <p>
        Existencias desde el último conteo físico de cada insumo. Los
        movimientos anteriores al conteo quedan fuera del saldo.
      </p>
      <Field title="Existencias al cierre del día">
        <input
          type="date"
          value={until}
          max={today()}
          onChange={(e) => {
            setUntil(e.target.value || today());
            setPage(1);
          }}
        />
      </Field>
      {message && <p role="status">{message}</p>}
      <Status {...balances} />
      {balances.data && (
        <div
          className="sd-inventory-scroll sd-table-scroll"
          role="region"
          aria-label="Existencias de bodega"
          tabIndex={0}
        >
          <table>
            <thead>
              <tr>
                {[
                  "Insumo",
                  "Unidad",
                  "Fecha de conteo",
                  "Conteo",
                  "Entradas",
                  "Devoluciones",
                  "Despachos",
                  "Bajas",
                  "Ajustes de salida",
                  "Existencia",
                  ...(canOperate ? ["Registrar"] : []),
                ].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {balances.data.rows.map((row) => (
                <tr key={row.itemId}>
                  <td>
                    <strong>{row.name}</strong>
                    <small>{row.code}</small>
                    {row.warning && (
                      <small className="sd-error">{row.warning}</small>
                    )}
                  </td>
                  <td>{row.unit}</td>
                  <td>{date(row.baselineDate) || "—"}</td>
                  {[
                    row.opening,
                    row.incoming,
                    row.returned,
                    row.dispatched,
                    row.disposed,
                    row.adjustments,
                    row.balance,
                  ].map((v, i) => (
                    <td key={i}>
                      {v == null
                        ? "—"
                        : v.toLocaleString("es-PA", {
                            maximumFractionDigits: 6,
                          })}
                    </td>
                  ))}
                  {canOperate && (
                    <td>
                      <div className="sd-toolbar">
                        <Button
                          label="Conteo físico"
                          className="p-button-text"
                          onClick={() => open(row, "CONTEO_FISICO")}
                        />
                        <Button
                          label="Entrada"
                          className="p-button-text"
                          disabled={!row.baselineDate || !row.active}
                          onClick={() => open(row, "ENTRADA")}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3>Conteos y entradas registrados</h3>
      <p>
        Las entregas, devoluciones y desincorporaciones se consultan en
        Histórico.
      </p>
      <Status {...history} />
      {history.data && (
        <>
          <div
            className="sd-inventory-scroll sd-table-scroll"
            role="region"
            aria-label="Movimientos de inventario"
            tabIndex={0}
          >
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Insumo</th>
                  <th>Cantidad</th>
                  <th>Motivo</th>
                  <th>Registró</th>
                </tr>
              </thead>
              <tbody>
                {history.data.rows.map((m) => (
                  <tr key={m._id}>
                    <td>{date(m.operationDate)}</td>
                    <td>
                      {m.type === "CONTEO_FISICO" ? "Conteo físico" : "Entrada"}
                    </td>
                    <td>
                      {m.code} · {m.name}
                    </td>
                    <td>
                      {m.quantity} {m.unit}
                    </td>
                    <td>{m.reason}</td>
                    <td>{m.createdBy?.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager data={history.data} page={page} setPage={setPage} />
        </>
      )}
      <Dialog
        header={
          form?.type === "CONTEO_FISICO"
            ? "Registrar conteo físico"
            : "Registrar entrada"
        }
        visible={!!form}
        onHide={() => !busy && setForm(null)}
        style={{ width: "min(95vw, 540px)" }}
        closable={!busy}
      >
        {form && (
          <form onSubmit={save}>
            <p>
              <strong>{form.name}</strong> · {form.unit}
            </p>
            {form.type === "CONTEO_FISICO" && (
              <p>
                Indique la existencia al inicio de la fecha elegida. Este conteo
                reemplaza el saldo anterior; las operaciones de ese día se suman
                o restan después. Solo se admite un conteo por insumo y día.
              </p>
            )}
            <Field title="Fecha de operación">
              <input
                required
                type="date"
                value={form.operationDate}
                max={today()}
                disabled={busy}
                onChange={(e) => change("operationDate", e.target.value)}
              />
            </Field>
            <Field title={`Cantidad (${form.unit})`}>
              <input
                required
                type="number"
                min={form.type === "ENTRADA" ? "0.000001" : "0"}
                step="any"
                value={form.quantity}
                disabled={busy}
                onChange={(e) => change("quantity", e.target.value)}
              />
            </Field>
            <Field title="Motivo / referencia">
              <textarea
                required
                maxLength={2000}
                value={form.reason}
                disabled={busy}
                onChange={(e) => change("reason", e.target.value)}
              />
            </Field>
            {error && (
              <p role="alert" className="sd-error">
                {error}
              </p>
            )}
            <div className="sd-toolbar">
              <Button
                type="button"
                label="Volver"
                className="p-button-text"
                disabled={busy}
                onClick={() => setForm(null)}
              />
              <Button
                type="submit"
                label={
                  form.type === "CONTEO_FISICO"
                    ? "Registrar conteo"
                    : "Registrar entrada"
                }
                loading={busy}
              />
            </div>
          </form>
        )}
      </Dialog>
    </section>
  );
}
